import { ItemType } from 'antd/es/menu/interface';
import { InputDataVersionType } from './types';
import { Button, Dropdown } from 'antd';
import { MoreVertical } from 'react-feather';
import { useCallback, useMemo } from 'react';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './InputDataActionsViewDropdown.translations.json';
import {
  InputDataKey,
  PopconfirmDelete,
  useInputDataDeleteMutation,
} from '@atlas-frontend/sdk';
import { useInputDataActions } from './useInputDataActions';

const { addTranslations, useTranslation } = i18nNsFactory(
  'input-data-actions-view-dropdown',
);
addTranslations(translations);

interface DropdownProps {
  version?: InputDataVersionType;
  versionId?: number | null;
}

export interface InputDataActionsViewDropdownProps extends DropdownProps {
  id?: number | null;
  subject?: string;
  valueDate?: string;
  onReload: () => Promise<void>;
}

export interface InputDataActionsViewDropdownInnerProps extends DropdownProps {
  id: number;
  subject: string;
  valueDate: string;
  onReload: () => Promise<void>;
}

export function InputDataActionsViewDropdown({
  id,
  subject,
  valueDate,
  version,
  ...rest
}: InputDataActionsViewDropdownProps) {
  if (version !== 'draft' || !id || !valueDate || !subject) {
    return null;
  }

  return (
    <InputDataActionsViewDropdownInner
      {...rest}
      id={id}
      subject={subject}
      valueDate={valueDate}
    />
  );
}

function InputDataActionsViewDropdownInner({
  id,
  subject,
  valueDate,
  versionId,
  onReload,
}: InputDataActionsViewDropdownInnerProps) {
  const { mutateAsync } = useInputDataDeleteMutation();
  const { onError, onSuccess, onLoad } = useInputDataActions(onReload);
  const { t } = useTranslation();

  const deleteDescriptionData = useCallback(() => {
    const data: InputDataKey = {
      dataStatus: 'draft',
      id,
      sampleType: 'description',
      subject,
      valueDate,
    };
    onLoad();
    mutateAsync({
      data,
    })
      .then(() => onSuccess())
      .catch(onError);
  }, [id, mutateAsync, onError, onLoad, onSuccess, subject, valueDate]);

  const items: ItemType[] = useMemo(
    () => [
      {
        key: 'delete-draft-version',
        label: (
          <PopconfirmDelete
            stopClickPropagation
            disabled={!versionId}
            onDelete={deleteDescriptionData}
            genitiveModelName={t('delete.genitive.description')}
          >
            {t('button.delete-draft')}
          </PopconfirmDelete>
        ),
      },
    ],
    [deleteDescriptionData, t, versionId],
  );

  return (
    <Dropdown menu={{ items }} trigger={['click']}>
      <Button
        id="input-data-actions-dropdown"
        type="text"
        shape="circle"
        icon={<MoreVertical />}
      />
    </Dropdown>
  );
}
